/* eslint-disable no-nested-ternary */
import styled from 'styled-components';
import { SelectProps } from './Select';

export const StyledSelectContainer = styled.div<{
  readonly error: SelectProps['error'];
  readonly $size: SelectProps['size'];
  readonly $isOpen: boolean;
  readonly $disabled: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  border-radius: 4px;
  gap: 4px;
  padding: ${({ $size }) => ($size === 'small' ? '0px 4px' : '8px 8px')};
  font-size: ${({ $size }) => ($size === 'small' ? 12 : 16)}px;
  height: ${({ $size }) => ($size === 'small' ? 26 : 40)}px;
  border: 1px solid
    ${({ error, $size, theme, $isOpen }) =>
      theme?.color
        ? theme.color(
            error
              ? '#DB0000'
              : $isOpen
                ? '#239ae7'
                : $size === 'small'
                  ? '#8D8D8D'
                  : '#B2B1BA',
          )
        : error
          ? '#DB0000'
          : $isOpen
            ? '#239ae7'
            : $size === 'small'
              ? '#8D8D8D'
              : '#B2B1BA'};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  background: ${({ $disabled }) => ($disabled ? '#f5f5f5' : 'transparent')};

  &:hover {
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 0.8)};
  }

  :focus {
    outline: none;
  }
`;

export const StyledMultiSelectContainer = styled(StyledSelectContainer)`
  padding: ${({ $size }) => ($size === 'small' ? '4px' : '8px')};
`;

export const SelectContent = styled.div<{ size: SelectProps['size'] }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  min-height: ${({ size }) => (size === 'small' ? 24 : 38)}px;
`;

export const SelectValues = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

export const MultiSelectTag = styled.span<{
  readonly $size: SelectProps['size'];
}>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: ${({ $size }) => ($size === 'small' ? '4px 8px' : '8px 12px')};
  border-radius: 4px;
  font-size: ${({ $size }) => ($size === 'small' ? 12 : 16)}px;
  background-color: rgba(35, 153, 231, 0.25);
`;

export const TagRemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 50%;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: rgba(35, 154, 231, 0.1);
  }
`;

export const SingleSelectValue = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: inherit;
`;

export const Placeholder = styled.span<{ readonly $size: SelectProps['size'] }>`
  color: ${({ theme }) => (theme?.color ? theme.color('#B2B1BA') : '#B2B1BA')};
  font-size: ${({ $size }) => ($size === 'small' ? 12 : 16)}px;
`;

export const SelectActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

export const ClearButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #b2b1ba;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #8d8d8d;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const ClearMultiTagButton = styled.button<{
  $size: SelectProps['size'];
}>`
  background: none;
  border: none;
  cursor: pointer;
  color: #b2b1ba;
  border-radius: 50%;
  padding: ${({ $size }) => ($size === 'small' ? '0px' : '2px')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $size }) => ($size === 'small' ? 10 : 14)}px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #bcbcbcff;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const ChevronIcon = styled.div<{ readonly $isOpen: boolean }>`
  color: #b2b1ba;
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const DropdownMenu = styled.div`
  position: absolute;
  z-index: 1000;
  margin-top: 2px;
  top: 100%;
  background-color: white;
  border: 1px solid #b2b1ba;
  border-radius: 4px;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-height: 200px;
  left: 0;
`;

export const SearchInput = styled.input<{
  readonly $size: SelectProps['size'];
  readonly $inputContentLength?: number;
}>`
  width: ${({ $inputContentLength }) =>
    $inputContentLength ? `${$inputContentLength}ch` : 'auto'};
  border: none;
  padding: ${({ $size }) => ($size === 'small' ? '4px 0px' : '6px 8px')};
  font-size: ${({ $size }) => ($size === 'small' ? 12 : 16)}px;
  outline: none;
  background: transparent;

  &::placeholder {
    color: ${({ theme }) =>
      theme?.color ? theme.color('#B2B1BA') : '#B2B1BA'};
  }
`;

export const OptionsList = styled.div`
  max-height: 160px;
  overflow-y: auto;
`;

export const Option = styled.div<{
  readonly $size: SelectProps['size'];
  readonly $isSelected: boolean;
}>`
  cursor: pointer;
  padding: ${({ $size }) => ($size === 'small' ? '6px 8px' : '8px 12px')};
  font-size: ${({ $size }) => ($size === 'small' ? 12 : 16)}px;
  background-color: ${({ $isSelected }) => ($isSelected ? '#e6f3ff' : 'white')};
  color: ${({ $isSelected }) => ($isSelected ? '#239ae7' : 'inherit')};
  transition: background-color 0.15s ease;
  border-left: ${({ $isSelected }) =>
    $isSelected ? '3px solid #239ae7' : '3px solid transparent'};

  &:hover {
    background-color: ${({ $isSelected }) =>
      $isSelected ? '#e6f3ff' : '#f5f5f5'};
  }
`;

export const OptionContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const OptionSelected = styled.span`
  color: #239ae7;
  font-weight: 600;
`;

export const NoOptionsMessage = styled.div<{
  readonly $size: SelectProps['size'];
}>`
  color: #b2b1ba;
  padding: ${({ $size }) => ($size === 'small' ? '6px 8px' : '8px 12px')};
  font-size: ${({ $size }) => ($size === 'small' ? 12 : 16)}px;
  text-align: center;
`;

export const StyledErrorMessage = styled.div<{
  errorPosition?: 'top' | 'bottom';
}>`
  color: #db0000;
  font-size: 12px;
  position: absolute;
  ${({ errorPosition }) =>
    errorPosition === 'top' ? 'bottom: 100%;' : 'top: 100%;'}
`;
